import { useMemo } from 'react';
import {
  CampaignStatus,
  CampaignSubscriptionWithCampaign,
  CampaignType as CampaignTypeDeprecated
} from 'services/campaign/types';
import { TabsValue } from '../constants/tabs';
import { CampaignListItem } from '../types/CampaignListItem';
import { CampaignType } from 'generated-graphql/graphql';

type Params = {
  tab: TabsValue;
  campaignsList: CampaignListItem[];
  campaignSubscriptions: CampaignSubscriptionWithCampaign[];
  filters: {
    filterId: string;
    value: string | boolean;
  }[];
  isChecked: boolean | null;
  userId: string | undefined;
  campaignIdsWithMoreTimeRequested?: Set<string>;
};

const campaignsTabTypes: (CampaignTypeDeprecated | CampaignType)[] = [
  CampaignTypeDeprecated.APPLICANT_OUTREACH,
  CampaignTypeDeprecated.NEW_CANDIDATE_OUTREACH,
  CampaignTypeDeprecated.HUMAN_OUTREACH,
  CampaignTypeDeprecated.ENGAGEMENT_OUTREACH
];

// TECH-DEBT: This hook is overly complex and should be refactored. It is difficult to understand what it does and how it works.
// We should move the logic to using the searchable filters.

/**
 * @deprecated
 */
export const useCampaignsList = ({
  tab,
  campaignsList,
  campaignSubscriptions,
  filters,
  isChecked,
  userId,
  campaignIdsWithMoreTimeRequested
}: Params) => {
  const annotateMoreTime = useMemo(
    () =>
      (campaign: CampaignListItem): CampaignListItem => {
        if (!campaignIdsWithMoreTimeRequested) return campaign;
        return {
          ...campaign,
          hasMoreTimeRequested:
            campaign.hasMoreTimeRequested ||
            campaignIdsWithMoreTimeRequested.has(campaign.id)
        };
      },
    [campaignIdsWithMoreTimeRequested]
  );

  const listToDisplay = useMemo(() => {
    const filterFunction = (campaign: CampaignListItem) => {
      let returnFilter = true;
      let hasArchivedFilter = false;

      // Hide campaigns that are marked as hidden unless filters are applied
      if (campaign.isHidden && filters.length === 0) {
        return false;
      }

      filters.forEach((filter) => {
        if (filter.filterId === 'status') {
          if (filter.value === CampaignStatus.LIVE) {
            returnFilter =
              returnFilter &&
              (campaign.status === CampaignStatus.LIVE ||
                campaign.status === CampaignStatus.FINISHED_SENDING);
          } else {
            if (filter.value === CampaignStatus.ARCHIVED) {
              hasArchivedFilter = true;
            }
            returnFilter = returnFilter && campaign.status === filter.value;
          }
        } else if (filter.filterId === 'needsReview') {
          if (filter.value === 'MORE_TIME_REQUESTED') {
            returnFilter =
              returnFilter && campaign.hasMoreTimeRequested === true;
          } else if (filter.value === true) {
            returnFilter = returnFilter && campaign.needsReview === true;
          }
        } else if (filter.filterId === 'createdBy') {
          returnFilter = returnFilter && campaign.userId === filter.value;
        }
      });

      if (!hasArchivedFilter) {
        returnFilter =
          returnFilter && campaign.status !== CampaignStatus.ARCHIVED;
      }

      if (tab === TabsValue.RECRUITMENT) {
        returnFilter =
          returnFilter &&
          (!!campaign.isParentInOtherCampaigns ||
            (!!campaign.type && campaignsTabTypes.includes(campaign.type)));
      }

      if (isChecked !== null && !isChecked) {
        returnFilter = returnFilter && campaign.userId === userId;
      }

      return returnFilter;
    };

    const campaignsFromSubscriptions = campaignSubscriptions.map(
      (s) => s.campaign
    );

    const listToFilter = [...campaignsList, ...campaignsFromSubscriptions].map(
      annotateMoreTime
    );

    return listToFilter.filter(filterFunction);
  }, [
    campaignSubscriptions,
    campaignsList,
    filters,
    tab,
    isChecked,
    userId,
    annotateMoreTime
  ]);

  const campaignsCount = useMemo(() => {
    const filterFunction = (campaign: CampaignListItem) => {
      return (
        campaign.type === CampaignTypeDeprecated.APPLICANT_OUTREACH ||
        campaign.type === CampaignTypeDeprecated.NEW_CANDIDATE_OUTREACH ||
        campaign.type === CampaignTypeDeprecated.ENGAGEMENT_OUTREACH
      );
    };

    const campaignsFromSubscriptions = campaignSubscriptions.map(
      (s) => s.campaign
    );

    return [...campaignsList, ...campaignsFromSubscriptions]?.filter(
      filterFunction
    ).length;
  }, [campaignsList, campaignSubscriptions]);

  return {
    list: listToDisplay,
    campaignsCount
  };
};
